import React from 'react';
import {
    List,
    Datagrid,
    TextField,
    DateField,
    FunctionField,
    Filter,
    TextInput,
    DateInput,
    SelectInput,
    TopToolbar,
    ExportButton,
    RefreshButton,
    useTranslate,
    NumberField,
    useListContext,
    RecordContextProvider,
} from 'react-admin';
import { Box, Chip, Typography, LinearProgress, useMediaQuery, useTheme } from '@mui/material';
import {
    Timeline,
    CheckCircle,
    Warning,
    Error as ErrorIcon,
    Speed,
    Bolt,
    Memory,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Filter component for LMS logs
const LmsLogFilter = (props: any) => {
    const translate = useTranslate();

    return (
        <Filter {...props}>
            <TextInput
                label={translate('resources.lms-logs.list.filters.search')}
                source="search"
                alwaysOn
            />
            <SelectInput
                label={translate('resources.lms-logs.list.filters.triggerReason')}
                source="triggerReason"
                choices={[
                    { id: 'SESSION_START', name: translate('resources.lms-logs.triggers.SESSION_START') },
                    { id: 'SESSION_STOP', name: translate('resources.lms-logs.triggers.SESSION_STOP') },
                    { id: 'STATUS_CHANGE', name: translate('resources.lms-logs.triggers.STATUS_CHANGE') },
                    { id: 'PERIODIC', name: translate('resources.lms-logs.triggers.PERIODIC') },
                    { id: 'MANUAL', name: translate('resources.lms-logs.triggers.MANUAL') },
                ]}
            />
            <SelectInput
                label={translate('resources.lms-logs.list.filters.allocationStrategy')}
                source="allocationStrategy"
                choices={[
                    { id: 'equal_distribution', name: translate('resources.lms-logs.strategies.equal_distribution') },
                    { id: 'queue', name: translate('resources.lms-logs.strategies.queue') },
                    { id: 'member_priority', name: translate('resources.lms-logs.strategies.member_priority') },
                ]}
            />
            <TextInput
                label={translate('resources.lms-logs.list.filters.loadGroupId')}
                source="loadGroupId"
            />
            <TextInput
                label={translate('resources.lms-logs.list.filters.ocppServerId')}
                source="ocppServerId"
            />
            <DateInput
                label={translate('resources.lms-logs.list.filters.startDate')}
                source="startDate"
            />
            <DateInput
                label={translate('resources.lms-logs.list.filters.endDate')}
                source="endDate"
            />
        </Filter>
    );
};

const DesktopListActions = () => (
    <TopToolbar>
        <RefreshButton />
        <ExportButton />
    </TopToolbar>
);

const MobileListActions = () => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, p: 0 }}>
        <RefreshButton />
        <ExportButton />
    </Box>
);

// Get trigger reason info
const getTriggerInfo = (reason: string) => {
    switch (reason) {
        case 'SESSION_START':
            return { color: 'success', icon: <CheckCircle /> };
        case 'SESSION_STOP':
            return { color: 'info', icon: <CheckCircle /> };
        case 'STATUS_CHANGE':
            return { color: 'warning', icon: <Warning /> };
        case 'PERIODIC':
            return { color: 'default', icon: <Timeline /> };
        case 'MANUAL':
            return { color: 'primary', icon: <Speed /> };
        default:
            return { color: 'default', icon: <Timeline /> };
    }
};

// Get allocation strategy color
const getStrategyColor = (strategy: string) => {
    switch (strategy) {
        case 'equal':
        case 'equal_distribution':
            return '#2196F3';
        case 'queue':
            return '#FF9800';
        case 'member_priority':
            return '#9C27B0';
        default:
            return '#757575';
    }
};

const strategyMap: { [key: string]: string } = {
    'equal': 'Equal Distribution',
    'equal_distribution': 'Equal Distribution',
    'queue': 'Queue (FCFS)',
    'member_priority': 'Member Priority',
};

const MobileList = () => {
    const { data, isLoading } = useListContext();
    const translate = useTranslate();
    const navigate = useNavigate();

    if (isLoading || !data) return null;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {data.map((record: any) => {
                const { color, icon } = getTriggerInfo(record.triggerReason);
                const totalAllocated = record.totalAllocatedCurrent || 0;
                const maxCurrent = record.loadGroup?.maxCurrent || record.maxCurrent || 40;
                const utilization = maxCurrent > 0 ? (totalAllocated / maxCurrent * 100) : 0;

                return (
                    <RecordContextProvider key={record.id} value={record}>
                        <Box
                            onClick={() => navigate(`/lms-logs/${record.id}/show`)}
                            sx={{
                                p: 2,
                                backgroundColor: '#fff',
                                borderRadius: 1,
                                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                                cursor: 'pointer',
                                '&:active': { backgroundColor: '#F5F5F5' },
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Chip
                                    label={translate(`resources.lms-logs.triggers.${record.triggerReason}`)}
                                    color={color as any}
                                    size="small"
                                    icon={icon}
                                    sx={{ fontWeight: 500 }}
                                />
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {record.error ? (
                                        <ErrorIcon sx={{ fontSize: 16, color: '#FF5252' }} />
                                    ) : (
                                        <CheckCircle sx={{ fontSize: 16, color: '#00C853' }} />
                                    )}
                                    <Typography variant="caption">
                                        {record.error ? translate('resources.lms-logs.status.error') : `${record.executionTimeMs || 0}ms`}
                                    </Typography>
                                </Box>
                            </Box>

                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {record.loadGroupName || record.loadGroupId}
                            </Typography>
                            {record.ocppServerName && (
                                <Typography variant="caption" color="text.secondary">
                                    {record.ocppServerName}
                                </Typography>
                            )}

                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 0.5 }}>
                                <Chip
                                    label={strategyMap[record.allocationStrategy] || record.allocationStrategy}
                                    size="small"
                                    icon={<Memory />}
                                    sx={{
                                        fontWeight: 500,
                                        backgroundColor: getStrategyColor(record.allocationStrategy) + '20',
                                        color: getStrategyColor(record.allocationStrategy),
                                    }}
                                />
                                <Typography variant="caption">{record.activeChargers || 0} Active</Typography>
                                <Typography variant="caption">
                                    {totalAllocated.toFixed(1)}A / {maxCurrent}A {utilization.toFixed(0)}%
                                </Typography>
                            </Box>

                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                {record.decisionTimestamp ? new Date(record.decisionTimestamp).toLocaleString() : ''}
                            </Typography>
                        </Box>
                    </RecordContextProvider>
                );
            })}
        </Box>
    );
};

export const LmsLogList = () => {
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <List
            filters={<LmsLogFilter />}
            actions={isMobile ? <MobileListActions /> : <DesktopListActions />}
            title={translate('resources.lms-logs.list.title')}
            perPage={25}
            sort={{ field: 'timestamp', order: 'DESC' }}
            sx={{
                '& .RaList-content': {
                    backgroundColor: isMobile ? 'transparent' : '#FAFAFA',
                },
                ...(isMobile && {
                    '& .RaList-main': { margin: 0 },
                    '& .RaList-content': { backgroundColor: 'transparent !important', boxShadow: 'none !important', mt: 2 },
                    '& .RaList-actions': { backgroundColor: 'transparent !important', p: '0 !important', minHeight: 'auto !important' },
                    '& .RaListToolbar-root': { backgroundColor: 'transparent !important', flexDirection: 'row !important', flexWrap: 'wrap !important', alignItems: 'center !important', gap: '8px !important' },
                    '& .RaListToolbar-root > span': { display: 'none' },
                    '& .RaFilterButton-root, & .add-filter': { display: 'none !important' },
                    '& .RaListToolbar-root > form, & .RaFilterForm-root': { flex: '1 1 100% !important', width: '100% !important' },
                    '& .filter-field, & .RaFilterForm-filterFormInput': { width: '100% !important' },
                    '& .filter-field .MuiFormControl-root': { width: '100% !important' },
                    '& .filter-field .MuiInputBase-root': { width: '100% !important' },
                    '& .MuiToolbar-root': { backgroundColor: 'transparent !important', minHeight: 'auto !important' },
                }),
            }}
        >
            {isMobile ? (
                <MobileList />
            ) : (
                <Datagrid
                    bulkActionButtons={false}
                    rowClick="show"
                    sx={{
                        '& .RaDatagrid-headerCell': {
                            backgroundColor: '#F5F5F5',
                            fontWeight: 600,
                        },
                        '& .RaDatagrid-row': {
                            '&:hover': {
                                backgroundColor: '#F0F7FF',
                                cursor: 'pointer',
                            },
                        },
                    }}
                >
                    <DateField
                        source="decisionTimestamp"
                        label={translate('resources.lms-logs.fields.decisionTimestamp')}
                        showTime
                        options={{
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                        }}
                    />

                    <FunctionField
                        label={translate('resources.lms-logs.fields.triggerReason')}
                        render={(record: any) => {
                            const { color, icon } = getTriggerInfo(record.triggerReason);
                            return (
                                <Chip
                                    label={translate(`resources.lms-logs.triggers.${record.triggerReason}`)}
                                    color={color as any}
                                    size="small"
                                    icon={icon}
                                    sx={{ fontWeight: 500 }}
                                />
                            );
                        }}
                    />

                    <FunctionField
                        label={translate('resources.lms-logs.fields.loadGroup')}
                        render={(record: any) => (
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {record.loadGroupName || record.loadGroupId}
                                </Typography>
                                {record.ocppServerName && (
                                    <Typography variant="caption" color="text.secondary">
                                        {record.ocppServerName}
                                    </Typography>
                                )}
                            </Box>
                        )}
                    />

                    <FunctionField
                        label={translate('resources.lms-logs.fields.allocationStrategy')}
                        render={(record: any) => {
                            return (
                                <Chip
                                    label={strategyMap[record.allocationStrategy] || record.allocationStrategy}
                                    size="small"
                                    icon={<Memory />}
                                    sx={{
                                        fontWeight: 500,
                                        backgroundColor: getStrategyColor(record.allocationStrategy) + '20',
                                        color: getStrategyColor(record.allocationStrategy),
                                    }}
                                />
                            );
                        }}
                    />

                    <FunctionField
                        label={translate('resources.lms-logs.fields.chargers')}
                        render={(record: any) => (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Bolt sx={{ fontSize: 18, color: '#00C853' }} />
                                <Box>
                                    <Typography variant="body2">
                                        {`${record.activeChargers || 0} Active`}
                                    </Typography>
                                    {record.pendingChargers > 0 && (
                                        <Typography variant="caption" color="text.secondary">
                                            {`${record.pendingChargers} Pending`}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        )}
                    />

                    <FunctionField
                        label={translate('resources.lms-logs.fields.powerAllocation')}
                        render={(record: any) => {
                            const totalAllocated = record.totalAllocatedCurrent || 0;
                            const totalRequested = record.totalRequestedCurrent || 0;
                            const available = record.availableCurrent || 0;
                            const maxCurrent = record.loadGroup?.maxCurrent || record.maxCurrent || 40;

                            // Calculate utilization based on allocated vs max load group current
                            const utilization = maxCurrent > 0 ? (totalAllocated / maxCurrent * 100) : 0;

                            // Show if power sharing is active (requested > available)
                            const isPowerSharing = totalRequested > available;

                            return (
                                <Box sx={{ minWidth: 140 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {totalAllocated.toFixed(1)}A / {maxCurrent}A
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 500,
                                                color: utilization > 90 ? '#FF5252' : utilization > 70 ? '#FF9800' : '#00C853'
                                            }}
                                        >
                                            {utilization.toFixed(0)}%
                                        </Typography>
                                    </Box>
                                    {isPowerSharing && (
                                        <Typography variant="caption" color="warning.main" display="block">
                                            Sharing: {totalRequested.toFixed(1)}A demand
                                        </Typography>
                                    )}
                                    <LinearProgress
                                        variant="determinate"
                                        value={Math.min(utilization, 100)}
                                        sx={{
                                            height: 6,
                                            borderRadius: 3,
                                            backgroundColor: '#E0E0E0',
                                            '& .MuiLinearProgress-bar': {
                                                backgroundColor: utilization > 90 ? '#FF5252' : utilization > 70 ? '#FF9800' : '#00C853',
                                                borderRadius: 3,
                                            },
                                        }}
                                    />
                                </Box>
                            );
                        }}
                    />

                    <FunctionField
                        label={translate('resources.lms-logs.fields.profiles')}
                        render={(record: any) => {
                            const sent = record.profilesSent || 0;
                            const accepted = record.profilesAccepted || 0;
                            const rejected = record.profilesRejected || 0;
                            const successRate = sent > 0 ? (accepted / sent * 100).toFixed(0) : 0;

                            return (
                                <Box>
                                    <Typography variant="body2">
                                        {`${accepted}/${sent} (${successRate}%)`}
                                    </Typography>
                                    {rejected > 0 && (
                                        <Typography variant="caption" color="error">
                                            {`${rejected} Rejected`}
                                        </Typography>
                                    )}
                                </Box>
                            );
                        }}
                    />

                    <FunctionField
                        label={translate('resources.lms-logs.fields.executionTime')}
                        render={(record: any) => {
                            const time = record.executionTimeMs || 0;
                            const isError = record.error;

                            return (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {isError ? (
                                        <>
                                            <ErrorIcon sx={{ fontSize: 18, color: '#FF5252' }} />
                                            <Typography variant="body2" color="error">
                                                {translate('resources.lms-logs.status.error')}
                                            </Typography>
                                        </>
                                    ) : (
                                        <>
                                            <CheckCircle sx={{ fontSize: 18, color: '#00C853' }} />
                                            <Typography variant="body2">
                                                {time}ms
                                            </Typography>
                                        </>
                                    )}
                                </Box>
                            );
                        }}
                    />

                    <FunctionField
                        label={translate('resources.lms-logs.fields.details')}
                        render={(record: any) => {
                            if (record.error) {
                                return (
                                    <Typography variant="body2" color="error">
                                        {record.error}
                                    </Typography>
                                );
                            }

                            if (record.triggerDetails && Object.keys(record.triggerDetails).length > 0) {
                                const details = record.triggerDetails;
                                if (details.chargerId) {
                                    return (
                                        <Typography variant="body2" color="text.secondary">
                                            {`Charger: ${details.chargerId}`}
                                        </Typography>
                                    );
                                }
                                if (details.inefficiency) {
                                    return (
                                        <Typography variant="body2" color="text.secondary">
                                            {`Inefficiency: ${details.inefficiency}%`}
                                        </Typography>
                                    );
                                }
                            }

                            return (
                                <Typography variant="body2" color="text.secondary">
                                    {translate('resources.lms-logs.details.noDetails')}
                                </Typography>
                            );
                        }}
                    />
                </Datagrid>
            )}
        </List>
    );
};
